/* eslint-disable react-hooks/exhaustive-deps */
// Check getHooksPropertyMap docs

import { useCallback, useMemo } from 'react'

import useFetchSuggestions from './useFetchSuggestions'
import useActivitySuggestions from './useActivitySuggestions'

import { AutocompleteDataSource, AutocompleteSuggestion } from 'types/shared'
import { ProjectFields } from '@yes.technology/react-toolkit'
import { FilteractionParams } from 'utils/apiTypes'

type Props = {
  parentPath?: string[]
  filteraction?: string
  filteractionParams?: FilteractionParams[]
  endpoint?: string
  showLoading?: boolean
  projectFields?: ProjectFields
  dataSources?: AutocompleteDataSource[]
  onSourceValuesChange?: (values: Record<string, string | undefined>) => void
  concatenationSourceMap?: string
}

type FetchSuggestionsParams = [value: string]

export type FetchSuggestionsHook = (Props: Props) => {
  suggestions: AutocompleteSuggestion[]
  isLoading: boolean
  fetchSuggestions: (...params: FetchSuggestionsParams) => void
  debouncedFetchSuggestions: (...params: FetchSuggestionsParams) => void
  resetSuggestions: () => void
  setSuggestions: React.Dispatch<React.SetStateAction<AutocompleteSuggestion[]>>
  onSourceValuesChange?: (values: Record<string, string | undefined>) => void
  hasAllSourceValues?: boolean
  initialSourceValuesSet?: boolean
}

const EMPTY_ARRAY: AutocompleteDataSource[] = []

export default function useSuggestionsFromSources(props: Props) {
  const { dataSources = EMPTY_ARRAY } = props

  const filteractionHook = useFetchSuggestions(props)
  const activityHook = useActivitySuggestions(props)

  const hookByDataSource: Record<
    string,
    ReturnType<FetchSuggestionsHook>
  > = useMemo(
    () => ({
      filteraction: filteractionHook,
      activity: activityHook
    }),
    [filteractionHook, activityHook]
  )

  // With getHooksPropertyMap we can set the correct dependencies for each memo/callback at runtime,
  // but React cannot verify it statically, so we are consciously disabling exhaustive-deps rule for this file
  const getHooksPropertyMap = (
    property: keyof ReturnType<FetchSuggestionsHook>
  ) => Object.values(hookByDataSource).map((hook) => hook[property])

  const suggestions = useMemo<AutocompleteSuggestion[]>(
    () =>
      dataSources?.flatMap(
        (dataSource) => hookByDataSource[dataSource.type]?.suggestions
      ) || [],
    [dataSources, ...getHooksPropertyMap('suggestions')]
  )

  const isLoading = dataSources?.some(
    (dataSource) => hookByDataSource[dataSource.type]?.isLoading
  )

  const fetchSuggestions = useCallback(
    (...params: FetchSuggestionsParams) => {
      dataSources?.flatMap((dataSource) =>
        hookByDataSource[dataSource.type]?.fetchSuggestions(...params)
      )
    },
    [dataSources, ...getHooksPropertyMap('fetchSuggestions')]
  )

  const debouncedFetchSuggestions = useCallback(
    (...params: FetchSuggestionsParams) => {
      dataSources?.flatMap((dataSource) =>
        hookByDataSource[dataSource.type]?.debouncedFetchSuggestions(...params)
      )
    },
    [dataSources, ...getHooksPropertyMap('debouncedFetchSuggestions')]
  )

  const resetSuggestions = useCallback(() => {
    dataSources?.flatMap((dataSource) =>
      hookByDataSource[dataSource.type]?.resetSuggestions()
    )
  }, [dataSources, ...getHooksPropertyMap('resetSuggestions')])

  const setSuggestions = useCallback(
    (suggestions: AutocompleteSuggestion[]) => {
      dataSources?.flatMap((dataSource) =>
        hookByDataSource[dataSource.type]?.setSuggestions(suggestions)
      )
    },
    [dataSources, ...getHooksPropertyMap('setSuggestions')]
  )

  return {
    suggestions,
    isLoading,
    fetchSuggestions,
    debouncedFetchSuggestions,
    resetSuggestions,
    setSuggestions,
    hasAllSourceValues: filteractionHook.hasAllSourceValues,
    initialSourceValuesSet: filteractionHook.initialSourceValuesSet
  }
}
